import { SetStateAction, useEffect } from 'react';
import { ace_index_hooks } from '@axo/shared/data-access/hooks';
import { useIsManager } from '@axo/shared/data-access/provider';
import { ace_index, permissions } from '@axo/shared/data-access/types';
import {
  FilterAppliedIndicator,
  SearchFilterCard,
} from '../../../components/SearchFilterCard';
import { WorkingListsPanel } from '../../../components/ManageWorkingLists';
import { ColumnID, SearchResults } from '../../../components/SearchResults';
import { LockedIcon } from '../../../components/SearchFilterCard/LockedIcon';
import { usePersistedState } from '../../../hooks/utils/usePersistedState';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import { useRelevantWorkingLists } from '../../../hooks/utils/useRelevantWorkingLists';
import { useSelectedWorkingList, useWorkingListActions } from '../../../store';

interface Props {
  marketCountry: string;
}

export function WorkingListsTab({ marketCountry }: Props) {
  const selected = useSelectedWorkingList();
  const { selectList } = useWorkingListActions();

  const { confirm } = useConfirmDialog();

  const isManager = useIsManager();

  const createList = ace_index_hooks.useCreateWorkingList();
  const updateList = ace_index_hooks.useUpdateWorkingList();
  const deleteList = ace_index_hooks.useDeleteWorkingList();

  const lists = useRelevantWorkingLists(marketCountry);

  const [mode, setMode] = usePersistedState(
    'ace.workinglist.mode.v1',
    'select' as 'select' | 'edit' | 'reorder',
    window.sessionStorage,
  );

  // listState captures a cloned state of the currently
  // selected working list. Whenever viewing, editing or
  // creating a new working list, this state will be
  // overwritten.
  const [listState, setListState] = usePersistedState(
    'ace.workinglist.listState.v1',
    undefined as ace_index.WorkingList | undefined,
    window.sessionStorage,
  );

  // Hook to make sure that the listState is kept properly
  // in sync with the currentListID.
  useEffect(() => {
    if (!selected) {
      setListState(undefined);
      return;
    }

    if (listState?.ID !== selected) {
      setListState(lists.data?.find((list) => list.ID === selected));
    }
  }, [selected, listState, setListState, lists.data]);

  const query: ace_index.FindInput = {
    ...(listState?.AceIndexQuery ?? {}),
    LoanApplicationMarketCountry: marketCountry,
  };

  return (
    <div>
      <WorkingListsPanel
        lists={lists.data ?? []}
        loading={lists.isLoading}
        mutating={
          updateList.isLoading || deleteList.isLoading || createList.isLoading
        }
        mode={mode}
        setMode={setMode}
        currentList={listState}
        setCurrentListID={selectList}
        onCreate={() => {
          const request = {
            Name: 'New list',
            Priority: lists.data
              ? Math.min(...lists.data.map((list) => list.Priority)) - 1
              : 0,
            Audience: {
              MarketCountries: ['NO', 'SE', 'FI', 'DK'],
              Roles: [permissions.RoleName.Manager],
            },
          } as ace_index.WorkingList;

          createList.mutateAsync(request).then((list) => {
            setListState(list);
            selectList(list.ID);
            setMode('edit');
          });
        }}
        editable={isManager}
        onEdit={(list) => {
          selectList(list.ID);
          setMode('edit');
        }}
        setEditState={(next) => setListState(setWorkingListDetails(next))}
        onSave={() => {
          if (!listState) {
            return;
          }

          updateList.mutateAsync(listState).then(() => setMode('select'));
        }}
        onCancel={() => {
          setListState(undefined);
          setMode('select');
        }}
        onDelete={(list) =>
          confirm({
            message: 'Are you sure you want to delete this list?',
            confirmText: 'Yes',
            cancelText: 'Cancel',
            confirmCallback: () =>
              deleteList.mutateAsync(list.ID).then(() => {
                if (list.ID === selected) {
                  selectList(undefined);
                }
              }),
          })
        }
      />

      {listState && mode !== 'reorder' && (
        <>
          {isManager && (
            <SearchFilterCard
              editable={mode === 'edit'}
              query={query}
              setQuery={(next) => setListState(setWorkingListQuery(next))}
              clearQuery={() => setListState(setWorkingListQuery({}))}
              marketCountry={marketCountry}
              cardProps={{
                id: 'WorkingList.StatusFilter',
                title: 'Filters',
                leftTag: (
                  <>
                    <LockedIcon title="Filter" />

                    <FilterAppliedIndicator
                      text={`Filter used by: ${listState.Name}`}
                    />
                  </>
                ),
              }}
            />
          )}

          <SearchResults
            sortable
            query={query}
            setQuery={(next) => setListState(setWorkingListQuery(next))}
            columns={(listState.Columns ?? defaultColumns) as ColumnID[]}
            setColumns={
              isManager && mode === 'edit'
                ? (next) => setListState(setWorkingListColumns(next))
                : undefined
            }
          />
        </>
      )}
    </div>
  );
}

const defaultColumns: ColumnID[] = [
  'HumanReadableID',
  'CompletedAt',
  'Status',
  'AppliedAmount',
  'FirstName',
  'LastName',
  'UpdatedAt',
  'Owner',
  'LockedBy',
];

function setWorkingListColumns(
  nextState: SetStateAction<ColumnID[]>,
): SetStateAction<ace_index.WorkingList | undefined> {
  return (prev) => {
    if (!prev) {
      return prev;
    }

    const next =
      typeof nextState === 'function'
        ? nextState(prev.Columns as ColumnID[])
        : nextState;

    return {
      ...prev,
      Columns: next,
    };
  };
}

function setWorkingListQuery(
  nextState: SetStateAction<ace_index.FindInput>,
): SetStateAction<ace_index.WorkingList | undefined> {
  return (prev) => {
    if (!prev) {
      return prev;
    }

    const next =
      typeof nextState === 'function'
        ? nextState(prev.AceIndexQuery)
        : nextState;

    return {
      ...prev,
      AceIndexQuery: next,
    };
  };
}

function setWorkingListDetails(
  nextState: SetStateAction<ace_index.WorkingList>,
): SetStateAction<ace_index.WorkingList | undefined> {
  return (prev) => {
    if (!prev) {
      return prev;
    }

    return typeof nextState === 'function' ? nextState(prev) : nextState;
  };
}
